import React, { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";

export default function Graphic() {
  const pageRef = useRef(null);

  useEffect(() => {
    const move = (e) => {
      if (!pageRef.current) return;

      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;

      pageRef.current.style.setProperty("--mx", x.toFixed(3));
      pageRef.current.style.setProperty("--my", y.toFixed(3));
    };

    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);

  return (
    <Page ref={pageRef}>
      <Noise />
      <Scanlines />
      <Vignette />

      <Poster>
        <MainGraphic>
          <CrestRow>
            <SideMark $flip>
              <CherubIcon />
            </SideMark>

            <CenterBadge>
              <Stars>✦ ✦ ★ ✦ ✦</Stars>
              <LaurelText>
                <span>‹</span>
                <strong>NFP</strong>
                <span>›</span>
              </LaurelText>
              <Stars>✦ ✦ ★ ✦ ✦</Stars>
            </CenterBadge>

            <SideMark>
              <CherubIcon />
            </SideMark>
          </CrestRow>

          <Title>NFP© DESIGN</Title>
          <Subtitle>NOBODY FOR PRESIDENT</Subtitle>

          <SmallCopy>
            Branding Dev ©2024
            <br />
            Hvntēr pty ltd
            <br />
            COPYRIGHT©NOBODY FOR PRESIDENT
          </SmallCopy>
        </MainGraphic>

        <BottomStamp>
          <MiniCherub $flip />
          <Globe>⊕</Globe>
          <MiniCherub />
        </BottomStamp>
      </Poster>
    </Page>
  );
}

/* ---------------- SVG GRAPHIC PARTS ---------------- */

function CherubIcon() {
  return (
    <svg viewBox="0 0 260 120" aria-hidden="true">
      <path
        fill="currentColor"
        d="M18 58c22-3 45-4 69-3 8-12 17-20 31-23 17-4 35 4 43 18 8-2 16-4 25-5 12-2 25-2 38-1 8 1 15 4 20 8 2 2 2 6-1 7-9 3-20 3-31 2-7 0-14 0-20 1 10 7 21 14 32 23 3 3 1 8-3 8-16-2-30-8-43-18-8 15-22 26-41 29-24 4-45-8-56-29-18 1-37 1-56 1-14 0-28 0-42-1-8-1-9-16-1-18Zm102-8c-5 2-8 6-9 12 7-2 14-3 21-3 2-6-5-12-12-9Zm-3 31c9 7 21 9 32 4-8-6-17-10-28-13-2 3-3 6-4 9Z"
      />
      <path
        fill="currentColor"
        d="M151 21c9 6 15 15 18 26l-15 4c-2-8-6-14-12-19l9-11Zm-58 11c-18 4-35 13-51 24 9 0 18 0 28-1 9-10 18-18 30-24l-7 1Zm130 14c10-2 21-2 32 0 4 1 5 8 0 10-10 4-21 4-32 4v-14Z"
      />
      <circle cx="129" cy="22" r="13" fill="currentColor" />
      <path
        fill="currentColor"
        d="M133 16c5 0 10 3 14 7-3-11-14-18-25-14-10 3-16 14-12 24 3-10 11-17 23-17Z"
      />
    </svg>
  );
}

function MiniCherub({ $flip }) {
  return (
    <MiniIcon $flip={$flip}>
      <CherubIcon />
    </MiniIcon>
  );
}

/* ---------------- ANIMATIONS ---------------- */

const grainMove = keyframes`
  0% { transform: translate3d(0, 0, 0); }
  25% { transform: translate3d(-3%, 2%, 0); }
  50% { transform: translate3d(2%, -4%, 0); }
  75% { transform: translate3d(-4%, -2%, 0); }
  100% { transform: translate3d(0, 0, 0); }
`;

const breathe = keyframes`
  0%, 100% { transform: translate3d(calc(var(--mx) * 6px), calc(var(--my) * 4px), 0) scale(1); }
  50% { transform: translate3d(calc(var(--mx) * 6px), calc(var(--my) * 4px), 0) scale(1.015); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

/* ---------------- STYLES ---------------- */

const Page = styled.main`
  --blue: #4f94ff;
  --blue-dark: #3475dc;
  --ink: #061018;
  --mx: 0;
  --my: 0;

  min-height: 100dvh;
  width: 100%;
  overflow: hidden;
  position: relative;
  background:
    radial-gradient(circle at 40% 18%, rgba(255, 255, 255, 0.12), transparent 28%),
    radial-gradient(circle at 70% 85%, rgba(0, 0, 0, 0.08), transparent 34%),
    linear-gradient(180deg, var(--blue), var(--blue-dark));
  color: var(--ink);
  display: grid;
  place-items: center;
`;

const Poster = styled.section`
  position: relative;
  z-index: 3;
  width: min(100%, 1120px);
  min-height: 100dvh;
  padding: clamp(38px, 6vw, 74px) 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MainGraphic = styled.div`
  width: min(92vw, 880px);
  margin-top: clamp(22px, 7vh, 74px);
  text-align: center;
  color: var(--ink);
  animation:
    ${fadeIn} 700ms ease both,
    ${breathe} 6s ease-in-out infinite;
  filter: contrast(1.25);
`;

const CrestRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: clamp(6px, 1.5vw, 20px);
  margin: 0 auto 16px;
`;

const SideMark = styled.div`
  color: var(--ink);
  display: flex;
  justify-content: ${({ $flip }) => ($flip ? "flex-start" : "flex-end")};
  transform: ${({ $flip }) => ($flip ? "scaleX(-1)" : "none")};
  opacity: 0.96;

  svg {
    width: clamp(110px, 24vw, 260px);
    height: auto;
    display: block;
  }
`;

const CenterBadge = styled.div`
  min-width: clamp(112px, 20vw, 230px);
  display: grid;
  gap: 4px;
  place-items: center;
`;

const Stars = styled.div`
  font-size: clamp(15px, 2vw, 28px);
  line-height: 0.9;
  letter-spacing: clamp(2px, 0.7vw, 8px);
  font-weight: 900;
`;

const LaurelText = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(6px, 1vw, 14px);
  font-family: Georgia, "Times New Roman", serif;
  font-weight: 900;
  line-height: 0.9;

  strong {
    font-size: clamp(42px, 8vw, 102px);
    letter-spacing: -0.08em;
  }

  span {
    font-size: clamp(54px, 8vw, 110px);
    font-weight: 900;
    transform: translateY(-3px);
  }
`;

const Title = styled.h1`
  margin: 12px 0 0;
  font-family: Impact, Haettenschweiler, "Arial Black", sans-serif;
  font-size: clamp(46px, 11vw, 142px);
  line-height: 0.85;
  letter-spacing: -0.045em;
  text-transform: uppercase;
`;

const Subtitle = styled.h2`
  margin: clamp(8px, 1.2vw, 16px) 0 0;
  font-family: Impact, Haettenschweiler, "Arial Black", sans-serif;
  font-size: clamp(19px, 4vw, 54px);
  line-height: 0.92;
  letter-spacing: -0.035em;
  text-transform: uppercase;
`;

const SmallCopy = styled.p`
  margin: clamp(10px, 1.5vw, 18px) 0 0;
  font-family: Arial, Helvetica, sans-serif;
  font-size: clamp(12px, 2vw, 26px);
  font-weight: 900;
  line-height: 1.08;
  letter-spacing: -0.045em;
`;

const BottomStamp = styled.div`
  margin-top: auto;
  margin-bottom: clamp(16px, 4vh, 42px);
  color: var(--ink);
  opacity: 0.88;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  transform: translate3d(calc(var(--mx) * -3px), calc(var(--my) * -2px), 0);
`;

const MiniIcon = styled.div`
  width: clamp(34px, 6vw, 72px);
  transform: ${({ $flip }) => ($flip ? "scaleX(-1)" : "none")};

  svg {
    width: 100%;
    display: block;
  }
`;

const Globe = styled.div`
  font-family: Georgia, serif;
  font-size: clamp(18px, 3vw, 38px);
  font-weight: 900;
  line-height: 1;
  transform: translateY(-1px);
`;

const Noise = styled.div`
  pointer-events: none;
  position: fixed;
  inset: -20%;
  z-index: 1;
  opacity: 0.32;
  mix-blend-mode: multiply;
  animation: ${grainMove} 0.55s steps(2) infinite;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 250 250' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.95'/%3E%3C/svg%3E");
  background-size: 180px 180px;
`;

const Scanlines = styled.div`
  pointer-events: none;
  position: fixed;
  inset: 0;
  z-index: 2;
  opacity: 0.13;
  background:
    repeating-linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.22) 0px,
      rgba(0, 0, 0, 0.22) 1px,
      transparent 1px,
      transparent 5px
    );
`;

const Vignette = styled.div`
  pointer-events: none;
  position: fixed;
  inset: 0;
  z-index: 2;
  background:
    radial-gradient(circle at center, transparent 45%, rgba(0, 0, 0, 0.18) 100%),
    linear-gradient(90deg, rgba(255, 255, 255, 0.08), transparent 14%, transparent 86%, rgba(0, 0, 0, 0.08));
`;